import {
  BLACK_COLOR,
  CabinetRange,
  CabinetSupport,
  CORNER_HANG_CABINET_HEIGHT_SURPLUS,
  CornerType,
  CrossPieceType,
  DEFAULT_SHELF_HEIGHT,
  DoorHandleType,
  FEET_EXTENSIONS_REFERENCE,
  GROUND_OFFSET,
  Material,
  MIN_WIDTH_NEED_REINFORCE,
  MIN_WIDTH_SIX_WHEEL,
  NATURAL_COLOR,
  PaneMaterial,
  PlateType,
  Position,
  PositionAngle,
  SABOT_HEIGHT,
  THICKNESS_WHEEL_PANE_BASE_SUPPORT_DEFAULT,
  WHEEL_BRAKE_REFERENCE,
  WHEEL_REFERENCE,
  WHITE_COLOR,
} from '../constants';

import { Shelf } from './Shelf';
import { CustomPiece, KewloxPieceType } from '../pieces/CustomPiece';
import { CabinetJson, SearchInfo } from '../Data';
import { Corner } from '../pieces/Corner';
import cloneDeep from 'lodash/cloneDeep';
import { Batten } from '../pieces/Batten';
import { getPiecesData } from '../utils/dataStorage';
import { Rack } from './Rack';
import { Color } from './Color';
import { PiecesList } from './PiecesList';
import { Top } from './Top';
import { v4 as uuidv4 } from 'uuid';
import { SimpleComponent } from '../pieces/SimpleComponent';
import { findColor } from '../utils/colorHelper';
import { getCabinetName, getCabinetNames } from '../utils/nameGenerator';
import {
  getCabinetDescription,
  getCabinetDescriptions,
} from '../utils/descriptionGenerator';
import { checkAndUpdate } from '../builder/change';
import { findPieceByRef, formatNumber } from '../utils/helpers';
import { UpperShelf } from '../pieces/UpperShelf';
import {
  acceptedCrossPieceTypesForTop,
  getDefaultCrossPieceType,
} from '../builder/crossPiecesManager';
import { Plate } from '../pieces/Plate';
import { WheelsBase } from '../pieces/WheelsBase';

export class Cabinet {
  private _id: string;
  private _prestashopId?: string;
  private _width: number;
  private _depth: number;
  private _reference?: string;
  private _fixToWall?: boolean;
  public range: CabinetRange;
  public corners: CustomPiece<Corner>[];
  public shelves: Shelf[];
  public top: Top;
  public feets?: CustomPiece<Batten>[];
  public feetExtensions?: CustomPiece<SimpleComponent>[];
  public wheels?: CustomPiece<SimpleComponent>[];
  public wheelPane?: CustomPiece<WheelsBase>;
  public thumbnail?: string;
  public image?: string;
  public upperShelf?: CustomPiece<UpperShelf>;

  constructor(
    width: number,
    depth: number,
    range: CabinetRange,
    corners: CustomPiece<Corner>[],
    shelves: Shelf[],
    top: Top,
    feets?: CustomPiece<Batten>[],
    id?: string,
    thumbnail?: string,
    feetExtensions?: CustomPiece<SimpleComponent>[],
    wheels?: CustomPiece<SimpleComponent>[],
    wheelPane?: CustomPiece<WheelsBase>,
    upperShelf?: CustomPiece<UpperShelf>,
  ) {
    this._width = width;
    this._depth = depth;
    this.range = range;
    this.top = top;
    if (feets) this.feets = feets;
    if (feetExtensions) this.feetExtensions = feetExtensions;
    if (wheels) this.wheels = wheels;
    if (wheelPane) this.wheelPane = wheelPane;
    this.corners = corners;
    this.shelves = shelves;
    if (id) this._id = id;
    else this._id = uuidv4();
    if (thumbnail) this.thumbnail = thumbnail;
    if (upperShelf) this.upperShelf = upperShelf;
    this._fixToWall = this.isFixedToWall();
  }

  static fromModel(model: CabinetJson): Cabinet {
    const pieces = getPiecesData();
    const { width, depth, range, thumbnail, image } = model;

    const corners: CustomPiece<Corner>[] = [];
    for (const item of model.corners) {
      corners.push(CustomPiece.fromModel<Corner>(item, pieces.corners));
    }
    corners.sort(CustomPiece.compareTo);

    const top: Top = Top.fromModel(model.top, pieces);

    const shelves: Shelf[] = [];
    const obj = new Cabinet(
      width,
      depth,
      range,
      corners,
      shelves,
      top,
      undefined,
      uuidv4(),
      thumbnail,
    );

    if (model.shelves) {
      for (const item of model.shelves) {
        shelves.push(Shelf.fromModel(item, obj));
      }
    }
    shelves.sort((a, b) => b.level - a.level);

    if (model.feets) {
      obj.feets = [];
      for (const item of model.feets) {
        obj.feets.push(CustomPiece.fromModel<Batten>(item, pieces.battens));
      }
      obj.feets.sort(CustomPiece.compareTo);

      if (model.feetExtensions) {
        obj.feetExtensions = [];
        for (const item of model.feetExtensions) {
          obj.feetExtensions.push(
            CustomPiece.fromModel<SimpleComponent>(item, pieces.others),
          );
        }
        obj.feetExtensions.sort(CustomPiece.compareTo);
      }
    }

    if (model.wheels) {
      obj.wheels = [];
      for (const item of model.wheels) {
        obj.wheels.push(
          CustomPiece.fromModel<SimpleComponent>(item, pieces.others),
        );
      }
      obj.wheels.sort(CustomPiece.compareTo);
      if (model.wheelPane) {
        obj.wheelPane = CustomPiece.fromModel<WheelsBase>(
          model.wheelPane,
          pieces.wheelsBases,
        );
      }
    }

    if (model.upperShelf) {
      obj.upperShelf = CustomPiece.fromModel<UpperShelf>(
        model.upperShelf,
        pieces.upperShelves,
      );
    }

    if (model.image) {
      obj.image = image;
    }

    if (model.id) {
      obj.reference = `${model.id}`;
    }

    return obj;
  }

  static replacer(key: string, value: any): any {
    // Filtering out properties for serialization
    const filterOut = ['id'];
    if (filterOut.includes(key)) {
      return undefined;
    }
    if (key[0] === '_') {
      return undefined;
    }
    return value;
  }

  toJSON() {
    return Object.assign({}, this, { width: this._width, depth: this._depth });
  }

  get height(): number {
    /**
     * Add corner height and support to get the height of cabinet
     */
    let height = 0;

    switch (this.support) {
      case CabinetSupport.AdjustableFeet:
        height += SABOT_HEIGHT;
        break;
      case CabinetSupport.WheelWhite:
      case CabinetSupport.WheelBlack:
        const wheelHeight = this.getWheel()?.height;
        if (wheelHeight) {
          height += wheelHeight;
        }
        break;
    }

    const corner = this.getCorner();
    if (corner) {
      const cornerHeight = corner.height || 0;
      // We add the 3mm difference between corner and total height
      height += cornerHeight + 3;
    }

    if (this.upperShelf) {
      height += this.upperShelf.height;
    }

    return height;
  }

  get depth() {
    return this._depth;
  }

  set depth(newDepth: number) {
    /**
     * Modification of depth of a cabinet trigger the update the the rack panes
     * and cross pieces
     */

    this._depth = newDepth;
    for (const shelf of this.shelves) {
      if (shelf.racks) {
        for (const rack of shelf.racks) {
          rack.updateDepth(newDepth);
        }
      }
      for (const drawer of shelf.drawers) {
        shelf.updateDrawer(drawer.position as number, null, null, newDepth);
      }

      for (const item of shelf.intermediatesShelves) {
        item.updateIntermediateShelf(undefined, newDepth);
        item.updateSupportIntermediateShelf(Position.Left, undefined, newDepth);
        item.updateSupportIntermediateShelf(
          Position.Right,
          undefined,
          newDepth,
        );
      }
    }

    // Update Top
    this.top.updateDepth(newDepth);
    if (this.wheelPane !== undefined) {
      this.addOrUpdateWheelSupportPane(undefined, newDepth);
    }
    if (this.upperShelf !== undefined) {
      this.addOrUpdateUpperShelf(undefined, newDepth);
    }
  }

  get width() {
    return this._width;
  }

  set width(newWidth: number) {
    /**
     * Change doors's shelf and rack's level 0 cross piece
     */
    this._width = newWidth;
    for (const shelf of this.shelves) {
      shelf.updateShelfWidth(newWidth);
    }

    if (this.wheels) {
      if (this.wheels.length > 4 && newWidth < MIN_WIDTH_SIX_WHEEL) {
        this.wheels = this.wheels.filter(
          e => e.position !== Position.Back && e.position !== Position.Front,
        );
      } else if (this.wheels.length === 4 && newWidth >= MIN_WIDTH_SIX_WHEEL) {
        this.addWheel(Position.Back);
        this.addWheel(Position.Front);
      }
    }

    if (this.wheelPane) {
      this.addOrUpdateWheelSupportPane(newWidth);
    }
    if (this.upperShelf) {
      this.addOrUpdateUpperShelf(newWidth);
    }

    this.top.updateWidth(newWidth);
  }

  get id() {
    return this._id;
  }

  set id(id: string) {
    this._id = id;
  }

  get prestashopId(): string | undefined {
    return this._prestashopId;
  }

  set prestashopId(value: string | undefined) {
    this._prestashopId = value;
  }

  get reference() {
    return this._reference;
  }

  set reference(reference: string | undefined) {
    this._reference = reference;
  }

  updateCabinetElements<T extends KewloxPieceType>(
    cabinetElements: CustomPiece<T>[],
    element: CustomPiece<T>,
  ): CustomPiece<T>[] {
    // Remove previous entry for this position
    cabinetElements = cabinetElements.filter(
      item => item.position !== element.position,
    );
    cabinetElements.push(element);
    return cabinetElements;
  }

  get price(): number {
    let price = 0;
    for (const item of this.corners) price += item.price;
    for (const item of this.shelves) price += item.price;
    if (this.feets) for (const item of this.feets) price += item.price;
    if (this.feetExtensions)
      for (const item of this.feetExtensions) price += item.price;
    if (this.wheels) for (const item of this.wheels) price += item.price;
    if (this.wheelPane) price += this.wheelPane.price;
    if (this.upperShelf) price += this.upperShelf.price;
    price += this.top.price;
    return price;
  }

  get salesPrice() {
    /**
     * Sale price Tax Exclude
     * Compute the by using the belgian vat and rounded it
     */
    return Math.round(this.price * 1.21) / 1.21;
  }

  getSalesPriceTaxInclude(vat: number, resellerRate: number) {
    /**
     * Price Tax Exclude
     */
    return this.salesPrice * resellerRate * vat;
  }

  displayPriceTaxInclude(
    vat = 1.21,
    resellerRate = 1,
    virgule = '.',
    thousenSeparator = ',',
  ): string {
    /**
     * We don't round if we have fraction because resallers don't use VAT price
     */
    return formatNumber(
      this.getSalesPriceTaxInclude(vat, resellerRate),
      virgule,
      thousenSeparator,
    );
  }

  get weight(): number {
    let weight = 0;
    for (const item of this.corners) weight += item.weight;
    for (const item of this.shelves) weight += item.weight;
    if (this.feets) for (const item of this.feets) weight += item.weight;
    if (this.feetExtensions)
      for (const item of this.feetExtensions) weight += item.weight;
    if (this.wheels) for (const item of this.wheels) weight += item.weight;
    if (this.wheelPane) weight += this.wheelPane.weight;
    if (this.upperShelf) weight += this.upperShelf.weight;
    weight += this.top.weight;
    return weight * 1000;
  }

  get weightKilo(): number {
    return this.weight / 1000;
  }

  clone(): Cabinet {
    const clonedCabinet: Cabinet = cloneDeep(this);
    for (const shelf of clonedCabinet.shelves) {
      shelf.cabinet = clonedCabinet;
    }

    return clonedCabinet;
  }

  makeCopy() {
    /**
     * Generate unique id when copying cabinet
     */
    const cabinetDuplicated: Cabinet = this.clone();
    cabinetDuplicated.id = uuidv4();
    return cabinetDuplicated;
  }

  getFeet(position = PositionAngle.FrontLeft): CustomPiece<Batten> | undefined {
    if (this.feets) return this.feets.find(element => element.position);
  }

  getCorner(
    position = PositionAngle.FrontLeft,
  ): CustomPiece<Corner> | undefined {
    return this.corners.find(element => element.position === position);
  }

  getFeetExtension(
    position = PositionAngle.FrontLeft,
  ): CustomPiece<SimpleComponent> | undefined {
    if (this.feetExtensions)
      return this.feetExtensions.find(element => element.position === position);
  }

  getWheel(
    position: PositionAngle | Position = PositionAngle.FrontLeft,
  ): CustomPiece<SimpleComponent> | undefined {
    if (this.wheels)
      return this.wheels.find(element => element.position === position);
  }

  getCornerHeight(
    position = PositionAngle.FrontLeft,
    support?: CabinetSupport,
  ) {
    // The corner height should be the total height of the shelves + feet
    let cornerHeight = 0;
    for (const shelf of this.shelves) {
      cornerHeight += shelf.realHeight;
    }

    if (
      this.support === CabinetSupport.Feet ||
      this.support === CabinetSupport.AdjustableFeet
    ) {
      const feet = this.getFeet(position);
      if (feet) {
        // @ts-ignore
        cornerHeight += feet.height;
      }
    }

    cornerHeight += this.top.height;

    // The corner have 3mm difference between total height
    cornerHeight -= 3;

    const cabinetSupport = support ?? this.support;

    if (
      cabinetSupport === CabinetSupport.ToHangHook &&
      (position === PositionAngle.BackRight ||
        position === PositionAngle.BackLeft)
    ) {
      cornerHeight += CORNER_HANG_CABINET_HEIGHT_SURPLUS;
    }

    return cornerHeight;
  }

  updateCorner(
    position: PositionAngle,
    height?: number | null,
    color?: Color | null,
    type?: CornerType,
    positionHole?: Position,
  ) {
    /**
     * Update only if the item exist previously
     */
    const item = this.getCorner(position);
    if (item) {
      const newHeight = height ?? item.height;
      const newcolor = color ?? item.color;
      const newType = type ?? item.base.type;
      let newPositionHole = undefined;
      if (newType === CornerType.Hook) {
        newPositionHole = positionHole ? positionHole : item.base.positionHole;
      }

      const searchInfo = Corner.getSearchInfos(
        newHeight,
        newcolor,
        newType,
        newPositionHole,
      );
      const customP = getPiecesData().corners.getCustom(searchInfo, position);
      this.corners = this.updateCabinetElements<Corner>(this.corners, customP);

      // Update if needed the feets batten to match the corners
      if (customP.color.reference === BLACK_COLOR) {
        if (
          this.getFeet() &&
          this.support === CabinetSupport.AdjustableFeet &&
          this.getFeet()?.base?.material !== Material.Black
        ) {
          const pieces = getPiecesData();
          for (const position of Object.values(PositionAngle)) {
            this.addOrUpdateFeet(position, pieces, height, Material.Black);
          }
        }
      } else {
        if (
          this.getFeet() &&
          this.getFeet()?.base?.material === Material.Black
        ) {
          const pieces = getPiecesData();
          if (this.support === CabinetSupport.AdjustableFeet) {
            for (const position of Object.values(PositionAngle)) {
              this.addOrUpdateFeet(
                position,
                pieces,
                height,
                Material.Aluminium,
              );
            }
          }
        }
      }
    }
  }

  updateCornersColor(color: Color) {
    for (const position of Object.values(PositionAngle)) {
      this.updateCorner(position, null, color);
    }
  }

  get support() {
    if (this.feets && this.feets.length > 0) {
      if (this.feetExtensions && this.feetExtensions.length > 0) {
        return CabinetSupport.AdjustableFeet;
      } else return CabinetSupport.Feet;
    }
    if (this.wheels && this.wheels.length > 0) {
      if (this.wheelPane?.color.reference === WHITE_COLOR)
        return CabinetSupport.WheelWhite;
      else return CabinetSupport.WheelBlack;
    }
    if (
      this.getCorner(PositionAngle.BackRight)?.base.type === CornerType.Hook
    ) {
      return CabinetSupport.ToHangHook;
    } else if (
      this.getCorner(PositionAngle.BackRight)?.base.type === CornerType.Front
    ) {
      return CabinetSupport.ToHangScrew;
    }
    return CabinetSupport.NoSupport;
  }

  setSupport(
    support: CabinetSupport,
    height = 0,
    material: Material = Material.Wood,
  ) {
    /**
     * Add or update the support to the cabinet
     */
    switch (support) {
      case CabinetSupport.ToHangHook:
      case CabinetSupport.ToHangScrew:
        this.feets = undefined;
        this.wheels = undefined;
        this.wheelPane = undefined;
        this.feetExtensions = undefined;
        const supportType =
          support === CabinetSupport.ToHangHook
            ? CornerType.Hook
            : CornerType.Front;

        this.updateCorner(
          PositionAngle.BackRight,
          this.getCornerHeight(PositionAngle.BackRight, support),
          undefined,
          supportType,
          support === CabinetSupport.ToHangHook ? Position.Right : undefined,
        );
        this.updateCorner(
          PositionAngle.BackLeft,
          this.getCornerHeight(PositionAngle.BackLeft, support),
          undefined,
          supportType,
          support === CabinetSupport.ToHangHook ? Position.Left : undefined,
        );
        this.updateCorner(
          PositionAngle.FrontLeft,
          this.getCornerHeight(PositionAngle.FrontLeft, support),
          undefined,
          CornerType.Front,
        );
        this.updateCorner(
          PositionAngle.FrontRight,
          this.getCornerHeight(PositionAngle.FrontRight, support),
          undefined,
          CornerType.Front,
        );
        break;
      case CabinetSupport.WheelWhite:
      case CabinetSupport.WheelBlack:
        this.feets = undefined;
        this.feetExtensions = undefined;
        for (const position of Object.values(PositionAngle)) {
          this.addWheel(position);
        }
        if (this.width >= MIN_WIDTH_SIX_WHEEL) {
          this.addWheel(Position.Back);
          this.addWheel(Position.Front);
        }
        if (support === CabinetSupport.WheelWhite) {
          this.addOrUpdateWheelSupportPane(
            undefined,
            undefined,
            findColor(WHITE_COLOR),
          );
        }

        if (support === CabinetSupport.WheelBlack) {
          this.addOrUpdateWheelSupportPane(
            undefined,
            undefined,
            findColor(BLACK_COLOR),
          );
        }
        break;
      case CabinetSupport.NoSupport:
        this.feets = undefined;
        this.wheels = undefined;
        this.wheelPane = undefined;
        this.feetExtensions = undefined;
        break;
      case CabinetSupport.Feet:
      case CabinetSupport.AdjustableFeet:
        this.wheels = undefined;
        this.wheelPane = undefined;
        const pieces = getPiecesData();
        if (support === CabinetSupport.AdjustableFeet) {
          for (const position of Object.values(PositionAngle)) {
            this.addFeetExtension(position);
          }
          if (this.getCorner()?.color?.reference === BLACK_COLOR) {
            material = Material.Black;
          } else {
            material = Material.Aluminium;
          }
        } else {
          this.feetExtensions = [];
        }
        for (const position of Object.values(PositionAngle)) {
          this.addOrUpdateFeet(position, pieces, height, material);
        }
        break;
    }

    // Reset the corners
    for (const corner of this.corners) {
      if (
        support !== CabinetSupport.ToHangHook &&
        support !== CabinetSupport.ToHangScrew
      ) {
        this.updateCorner(
          corner.position as PositionAngle,
          this.getCornerHeight(corner.position as PositionAngle, support),
          undefined,
          CornerType.Normal,
        );
      }
    }
    if (support !== CabinetSupport.ToHangScrew)
      this.setDrillCrossPiece(false, !!this._fixToWall);
    else this.setDrillCrossPiece(true);
  }

  isHanging() {
    return (
      this.support === CabinetSupport.ToHangHook ||
      this.support === CabinetSupport.ToHangScrew
    );
  }

  addOrUpdateFeet(
    position: PositionAngle,
    pieces: PiecesList,
    height?: number | null,
    material?: Material | null,
  ) {
    /**
     * Update only if the item previously exist
     */
    const item = this.getFeet(position);
    let searchInfo: SearchInfo;
    if (item) {
      const newHeight = height ? height : item.height;
      const newMaterial = material ? material : item.base.material;
      searchInfo = Batten.getSearchInfos(newHeight, newMaterial, true);
    } else {
      if (!height || !material)
        throw new Error(
          'height and material params mandatory for adding new feet to cabinet',
        );
      searchInfo = Batten.getSearchInfos(height, material, true);
    }
    const customP = pieces.battens.getCustom(searchInfo, position);
    if (this.feets) {
      this.feets = this.updateCabinetElements<Batten>(this.feets, customP);
    } else {
      this.feets = [customP];
    }
  }

  private addFeetExtension(position: PositionAngle) {
    if (!this.getFeetExtension(position)) {
      const pieces = getPiecesData();
      const feetExtension = findPieceByRef(FEET_EXTENSIONS_REFERENCE);
      if (!feetExtension) {
        throw new Error('There is no feet extension in piece list');
      }
      const customP = new CustomPiece<SimpleComponent>(
        feetExtension,
        false,
        position,
      );
      if (this.feetExtensions)
        this.feetExtensions = this.updateCabinetElements<SimpleComponent>(
          this.feetExtensions,
          customP,
        );
      else {
        this.feetExtensions = [customP];
      }
    }
  }

  private addWheel(position: PositionAngle | Position) {
    if (!this.getWheel(position)) {
      let wheel = findPieceByRef(WHEEL_REFERENCE);

      if (
        position === PositionAngle.FrontLeft ||
        position === PositionAngle.FrontRight
      ) {
        // In Kewlox manual, front wheel should be the one with brake
        wheel = findPieceByRef(WHEEL_BRAKE_REFERENCE);
      }
      if (!wheel) {
        throw new Error('There is no wheel in piece list');
      }
      const customP = new CustomPiece<SimpleComponent>(wheel, false, position);
      if (this.wheels)
        this.wheels = this.updateCabinetElements<SimpleComponent>(
          this.wheels,
          customP,
        );
      else {
        this.wheels = [customP];
      }
    }
  }

  public addOrUpdateWheelSupportPane(
    width?: number,
    depth?: number,
    color?: Color,
    drilling: PlateType = PlateType.DoorsPane,
  ) {
    if (!color) {
      if (!this.wheelPane) {
        throw new Error('Should have a color to update wheelPane');
      }
      color = this.wheelPane?.color;
    }
    const searchInfo = WheelsBase.getSearchInfos(
      depth ?? this.depth,
      width ?? this.width,
      PaneMaterial.MDF,
      color,
      THICKNESS_WHEEL_PANE_BASE_SUPPORT_DEFAULT,
      drilling,
    );
    const customP = getPiecesData().wheelsBases.getCustom(
      searchInfo,
      Position.Front,
    );
    if (customP) {
      this.wheelPane = customP;
    } else {
      throw new Error('Could not find the support pane for wheel');
    }
  }

  removeUpperShelf() {
    /**
     * Remove upperShelf
     */
    this.upperShelf = undefined;
  }

  addOrUpdateUpperShelf(width?: number, depth?: number, color?: Color) {
    if (!color) {
      if (!this.upperShelf) {
        throw new Error('Should have a color to update upperShelf');
      }
      color = this.upperShelf?.color;
    }
    const searchInfo = UpperShelf.getSearchInfos(
      width ?? this.width,
      depth ?? this.depth,
      color,
    );
    const customP = getPiecesData().upperShelves.getCustom(
      searchInfo,
      Position.Front,
    );
    if (customP) {
      this.upperShelf = customP;
    } else {
      throw new Error('Could not find the upper shelf');
    }
  }

  addShelf(level?: number): Shelf {
    /**
     * If no shelf exist, add a default shelf
     * otherwise duplicate the level or the last if no level specified
     */
    let newShelf: Shelf;
    let newPosition: number;

    if (level !== undefined) newPosition = level + 1;
    else newPosition = this.shelves.length;

    if (!this.shelves || this.shelves.length === 0) {
      this.shelves = [];
      // TODO: complete the default shelf and rack with relevant pieces
      const rack = new Rack(0, DEFAULT_SHELF_HEIGHT, [], [], []);
      newShelf = new Shelf(this.shelves.length, false, false, [rack], [], this);
    } else {
      if (level === undefined) level = this.shelves.length - 1;
      if (level >= 0 && level < this.shelves.length) {
        const shelf = this.shelf(level);
        newShelf = shelf.clone();
        newShelf.cabinet = this;
        if (newShelf.cabinet.isHanging()) {
          if (newShelf.hasDrawers()) {
            newShelf.drawers = [];
          }
          newShelf
            .rack(0)
            .updateCrossPiece(
              Position.Back,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              false,
            );
        }
      } else {
        throw new Error(
          `Can't add a shelf based on the level ${level}, out of range`,
        );
      }
    }

    newShelf.level = this.shelves.length;
    this.shelves.push(newShelf);
    this.moveShelfPosition(
      newShelf.level,
      newPosition - this.shelves.length + 1,
    );

    // // max is based on height of corners angle
    // if (this.getCornerHeight() > MAX_CORNER_HEIGHT) {
    //   throw new Error(`Maximum corner height reached for this cabinet`);
    // }

    this.updateCornerHeight();

    checkAndUpdate(this);

    return newShelf;
  }

  updateCornerHeight() {
    // Update the height of the corner
    this.updateCorner(
      PositionAngle.FrontLeft,
      this.getCornerHeight(PositionAngle.FrontLeft),
    );
    this.updateCorner(
      PositionAngle.BackLeft,
      this.getCornerHeight(PositionAngle.BackLeft),
    );
    this.updateCorner(
      PositionAngle.FrontRight,
      this.getCornerHeight(PositionAngle.FrontRight),
    );
    this.updateCorner(
      PositionAngle.BackRight,
      this.getCornerHeight(PositionAngle.BackRight),
    );
  }

  removeShelf(level: number) {
    if (this.shelves.length > 1) {
      if (level >= 0 && level < this.shelves.length) {
        const shelf = this.shelf(level);
        if (shelf.hasBackDoor()) {
          shelf.removeDoor(PositionAngle.BackLeft);
          shelf.removeDoor(PositionAngle.BackRight);
        }
      }

      this.shelves = this.shelves.filter(shelf => shelf.level !== level);
      for (const shelf of this.shelves) {
        if (shelf.level > level) {
          shelf.level -= 1;
        }
      }
    } else {
      throw new Error('A cabinet should have at least one shelf');
    }
    this.shelves.sort((a: Shelf, b: Shelf) => a.level - b.level);

    this.updateCornerHeight();
    checkAndUpdate(this);
  }

  moveShelfPosition(level: number, n = 1) {
    /**
     * Update the position of the shelf in the cabinet and update levels
     * n: the switch position, if negative, the shelf go down n time
     *    if positive, the shelf go up, n time
     */
    if (this.shelves.length === 0)
      throw new Error('A cabinet should have at least one shelf');

    if (n === 0) return;

    const shelf =
      level >= 0 && level < this.shelves.length ? this.shelf(level) : undefined;
    if (!shelf || level + n >= this.shelves.length || level + n < 0)
      throw new Error('Invalid position for switching shelf');

    if (n > 0) {
      for (let i = level + 1; i <= level + n; i++) {
        if (i >= 0 && i < this.shelves.length) {
          this.shelf(i).level -= 1;
        }
      }
    } else {
      for (let i = level - 1; i >= level + n; i--) {
        if (i >= 0 && i < this.shelves.length) {
          this.shelf(i).level += 1;
        }
      }
    }

    shelf.level = level + n;
    this.shelves.sort((a: Shelf, b: Shelf) => a.level - b.level);

    const newLevel = level + n;
    if (newLevel - 1 >= 0) {
      this.shelf(newLevel - 1).resetBackCrossPiece();
      this.shelf(newLevel - 1)
        .rack(0)
        .updateCrossPiece(
          Position.Back,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          false,
        );
    }
    this.shelf(newLevel).resetBackCrossPiece();

    this.shelf(newLevel)
      .rack(0)
      .updateCrossPiece(
        Position.Back,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        false,
      );
    checkAndUpdate(this);
  }

  isValid(): boolean {
    //TODO: Implement me
    return true;
  }

  cornersColor() {
    const frontLeftCorner = this.getCorner(PositionAngle.FrontLeft);
    if (!frontLeftCorner) return;
    for (const corner of this.corners)
      if (corner.color !== frontLeftCorner.color) return;
    return frontLeftCorner.color;
  }

  shelf(level: number): Shelf {
    /**
     * return the cabinet's shelf at the level of param
     */
    const shelf = this.shelves.find(item => item.level === level);
    if (!shelf) throw new Error(`Shelf not exist for level ${level}`);
    return shelf;
  }

  lastShelf(): Shelf {
    /**
     * return the cabinet's shelf at the level of param
     */
    const shelf = this.shelves.find(
      item => item.level === this.numberOfShelves - 1,
    );
    if (!shelf) throw new Error(`Shelf not exist for level last`);
    return shelf;
  }

  get numberOfShelves(): number {
    return this.shelves.length;
  }

  get groundOffset(): number {
    if (this.isHanging()) return GROUND_OFFSET;
    return 0;
  }

  get bottomSupportHeight(): number {
    let height = 0;
    switch (this.support) {
      case CabinetSupport.WheelBlack:
      case CabinetSupport.WheelWhite:
        const wheelHeight = this.getWheel()?.height;
        if (wheelHeight) {
          height += wheelHeight;
        }
        break;
      case CabinetSupport.Feet:
      case CabinetSupport.AdjustableFeet:
        const feet = this.getFeet();
        if (feet) {
          height += feet.height;
          if (this.support === CabinetSupport.AdjustableFeet)
            height += SABOT_HEIGHT;
        }
        break;
      default:
        break;
    }

    return height;
  }

  setDrillCrossPiece(value: boolean, ignoreLastCrossPiece?: boolean) {
    this.shelves.find(s => s.level === 0)!.setDrillRackCrossPiece(value);
    if (!ignoreLastCrossPiece) this.top.setDrillCrossPiece(value);
  }

  // Used by rules:
  hasUpperShelfOtherThanAllowed() {
    if (
      this.upperShelf &&
      this.upperShelf.color.reference !== WHITE_COLOR &&
      this.upperShelf.color.reference !== BLACK_COLOR &&
      this.upperShelf.color.reference !== NATURAL_COLOR
    )
      return true;

    return false;
  }

  hasWheels(): boolean {
    return !!(this.wheels && this.wheels.length > 0);
  }

  hasDrawers() {
    for (const shelf of this.shelves) {
      if (shelf.hasDrawers()) return true;
    }
    return false;
  }

  hasNaturalPlates() {
    if (
      this.top.plate &&
      this.top.plate.color.reference === NATURAL_COLOR &&
      !this.top.glassPlate
    )
      return true;
    for (const shelf of this.shelves) {
      if (shelf.racks) {
        for (const rack of shelf.racks) {
          if (
            rack.plate &&
            rack.plate.color.reference === NATURAL_COLOR &&
            !rack.glassPlate
          )
            return true;
        }
      }
    }
    return false;
  }

  hasPartialOrTotalTopGlassPlate() {
    return (
      this.top.plate &&
      (this.top.plate.color.material === PaneMaterial.Glass ||
        this.top.glassPlate)
    );
  }

  changeReinforceTopFrontCrossPiece(reinforce: boolean) {
    const frontCrossPiece = this.top.getCrossPiece(Position.Front);
    if (frontCrossPiece) {
      this.top.updateCrossPiece(
        Position.Front,
        this.width,
        undefined,
        undefined,
        reinforce,
      );
    }
  }

  numberOfDoorButtonsInMDFDoor() {
    let nbDoorButtonsInMDFDoor = 0;
    for (const shelf of this.shelves) {
      for (const door of shelf.doors) {
        if (
          (door.doorHandleType === DoorHandleType.Button ||
            door.doorHandleType === DoorHandleType.BigButton) &&
          door.material === PaneMaterial.MDF
        )
          nbDoorButtonsInMDFDoor++;
      }
    }
    return nbDoorButtonsInMDFDoor;
  }

  hasMirrorPane(): boolean {
    if (this.top.plate && this.top.plate.material === PaneMaterial.Mirror)
      return true;

    for (const shelf of this.shelves) {
      if (shelf.doors) {
        for (const door of shelf.doors) {
          if (door.material === PaneMaterial.Mirror) return true;
        }
      }
      if (shelf.racks) {
        for (const rack of shelf.racks) {
          if (rack.plate && rack.plate.material === PaneMaterial.Mirror)
            return true;

          if (rack.panes) {
            for (const pane of rack.panes) {
              if (pane.material === PaneMaterial.Mirror) return true;
            }
          }
        }
      }
    }

    return false;
  }

  hasPlateInAluminium(): boolean {
    if (this.top.plate && this.top.plate.material === PaneMaterial.Alu)
      return true;

    for (const shelf of this.shelves) {
      if (shelf.racks) {
        for (const rack of shelf.racks) {
          if (rack.plate && rack.plate.material === PaneMaterial.Alu)
            return true;
        }
      }
    }

    return false;
  }

  hasPlateWithGlassOrMirror(): boolean {
    if (this.top.glassPlate) return true;

    if (
      this.top.plate &&
      (this.top.plate.material === PaneMaterial.Glass ||
        this.top.plate.material === PaneMaterial.Mirror)
    )
      return true;

    for (const shelf of this.shelves) {
      if (shelf.racks) {
        for (const rack of shelf.racks) {
          if (rack.glassPlate) return true;

          if (
            rack.plate &&
            (rack.plate.material === PaneMaterial.Glass ||
              rack.plate.material === PaneMaterial.Mirror)
          )
            return true;
        }
      }
    }

    return false;
  }

  hasPlateColorChangedComparedTo(
    other: Cabinet,
    toMDFWithoutGlass: boolean = false,
  ) {
    if (
      this.top.plate &&
      other.top.plate &&
      this.top.plate.color.reference !== other.top.plate.color.reference &&
      (!toMDFWithoutGlass ||
        (this.top.plate.material === PaneMaterial.MDF && !this.top.glassPlate))
    )
      return true;

    if (this.shelves.length !== other.shelves.length) return false;

    for (const shelf of this.shelves) {
      const otherShelf = other.shelf(shelf.level);
      if (
        shelf.racks &&
        otherShelf.racks &&
        shelf.racks.length === otherShelf.racks.length
      ) {
        for (const rack of shelf.racks) {
          const otherRack = otherShelf.rack(rack.level);
          if (
            rack.plate &&
            otherRack.plate &&
            rack.plate.color.reference !== otherRack.plate.color.reference &&
            (!toMDFWithoutGlass ||
              (rack.plate.material === PaneMaterial.MDF && !rack.glassPlate))
          )
            return true;
        }
      }
    }

    return false;
  }

  hasReinforcedCrossPiece() {
    for (const cp of this.top.crossPieces) {
      if (cp.base.reinforce) return true;
    }

    for (const shelf of this.shelves) {
      for (const rack of shelf.racks) {
        for (const cp of rack.crossPieces) {
          if (cp.base.reinforce) return true;
        }
      }
    }

    return false;
  }

  hasTopFrontCrossPiece(reinforced = false) {
    const cp = this.top.getCrossPiece(Position.Front);

    return (
      !!cp &&
      (!reinforced || cp.base.reinforce) &&
      (reinforced || !cp.base.reinforce)
    );
  }

  hasCrossPieceForDoors() {
    for (const cp of this.top.crossPieces) {
      if (
        cp.base.type === CrossPieceType.UpperDoorsLowerDoors ||
        cp.base.type === CrossPieceType.UpperEmptyLowerDoors
      )
        return true;
    }

    for (const shelf of this.shelves) {
      for (const rack of shelf.racks) {
        for (const cp of rack.crossPieces) {
          if (
            cp.base.type === CrossPieceType.UpperDoorsLowerDoors ||
            cp.base.type === CrossPieceType.UpperEmptyLowerDoors
          )
            return true;
        }
      }
    }

    return false;
  }

  changeColorTopPlateTo(colorRef: string) {
    if (!this.top.plate) {
      this.top.plate = Plate.standardPlate(
        this.depth,
        this.width,
        Position.Front,
      );
    }
    this.top.updatePlate(
      undefined,
      undefined,
      PaneMaterial.MDF,
      findColor(colorRef),
      false,
    );
  }

  changeColorNaturalPlatesTo(other: Cabinet, colorRef: string) {
    if (
      this.top.plate &&
      other.top.plate &&
      this.top.plate.color.reference !== other.top.plate.color.reference &&
      this.top.plate.material === PaneMaterial.MDF &&
      this.top.plate.color.reference === NATURAL_COLOR &&
      !this.top.glassPlate
    )
      this.top.updatePlate(
        undefined,
        undefined,
        undefined,
        findColor(colorRef),
      );

    if (this.shelves.length !== other.shelves.length) return;

    for (const shelf of this.shelves) {
      const otherShelf = other.shelf(shelf.level);
      if (
        shelf.racks &&
        otherShelf.racks &&
        shelf.racks.length === otherShelf.racks.length
      ) {
        for (const rack of shelf.racks) {
          const otherRack = otherShelf.rack(rack.level);
          if (
            rack.plate &&
            otherRack.plate &&
            rack.plate.color.reference !== otherRack.plate.color.reference &&
            rack.plate.color.reference === NATURAL_COLOR &&
            rack.plate.material === PaneMaterial.MDF &&
            !rack.glassPlate
          )
            rack.updatePlate(
              undefined,
              undefined,
              undefined,
              findColor(colorRef),
            );
        }
      }
    }
  }

  addGlassPlateWhenColorChangedComparedTo(other: Cabinet) {
    if (
      this.top.plate &&
      other.top.plate &&
      this.top.plate.color.reference !== other.top.plate.color.reference &&
      this.top.plate.material === PaneMaterial.MDF &&
      !this.top.glassPlate
    )
      this.top.updatePlate(undefined, undefined, undefined, undefined, true);

    if (this.shelves.length !== other.shelves.length) return;

    for (const shelf of this.shelves) {
      const otherShelf = other.shelf(shelf.level);
      if (
        shelf.racks &&
        otherShelf.racks &&
        shelf.racks.length === otherShelf.racks.length
      ) {
        for (const rack of shelf.racks) {
          const otherRack = otherShelf.rack(rack.level);
          if (
            rack.plate &&
            otherRack.plate &&
            rack.plate.color.reference !== otherRack.plate.color.reference &&
            rack.plate.material === PaneMaterial.MDF &&
            !rack.glassPlate
          )
            rack.updatePlate(undefined, undefined, undefined, undefined, true);
        }
      }
    }
  }

  hasAddedGlassPlateComparedTo(other: Cabinet) {
    if (
      this.top.plate &&
      other.top.plate &&
      this.top.glassPlate &&
      !other.top.glassPlate
    )
      return true;

    if (this.shelves.length !== other.shelves.length) return false;

    for (const shelf of this.shelves) {
      const otherShelf = other.shelf(shelf.level);
      if (
        shelf.racks &&
        otherShelf.racks &&
        shelf.racks.length === otherShelf.racks.length
      ) {
        for (const rack of shelf.racks) {
          const otherRack = otherShelf.rack(rack.level);
          if (
            rack.plate &&
            otherRack.plate &&
            rack.glassPlate &&
            !otherRack.glassPlate
          )
            return true;
        }
      }
    }

    return false;
  }

  numberOfDrawers20cmHeight() {
    let nbDrawers20cm = 0;
    for (const shelf of this.shelves) {
      for (const drawer of shelf.drawers) {
        if (drawer.height === 200) nbDrawers20cm++;
      }
    }
    return nbDrawers20cm;
  }

  numberOfDrawersMoreThan20cmHeight() {
    let nbDrawersMoreThan20cm = 0;
    for (const shelf of this.shelves) {
      for (const drawer of shelf.drawers) {
        if (drawer.height > 200) nbDrawersMoreThan20cm++;
      }
    }
    return nbDrawersMoreThan20cm;
  }

  numberOfDrawersUpTo20cmHeight() {
    let nbDrawersUpTo20cm = 0;
    for (const shelf of this.shelves) {
      for (const drawer of shelf.drawers) {
        if (drawer.height <= 200) nbDrawersUpTo20cm++;
      }
    }
    return nbDrawersUpTo20cm;
  }

  hasBottomBackCrossPieceDrilled() {
    const cp = this.shelf(0)
      .rack(0)
      .getCrossPiece(Position.Back);
    return !!cp?.base.drill;
  }

  hasTopBackCrossPieceDrilled() {
    const cp = this.top.getCrossPiece(Position.Back);
    return !!cp?.base.drill;
  }

  addBackCrossPiecesDrilled() {
    const cp = this.top.getCrossPiece(Position.Back);
    if (!cp)
      this.top.addCrossPiece(
        Position.Back,
        this.width,
        undefined,
        CrossPieceType.UpperEmptyLowerPane,
        false,
        false,
        true,
      );

    const cpBottom = this.shelf(0)
      .rack(0)
      .getCrossPiece(Position.Back);
    if (!cpBottom)
      this.shelf(0)
        .rack(0)
        .addCrossPiece(
          Position.Back,
          this.width,
          undefined,
          CrossPieceType.UpperEmptyLowerPane,
          false,
          false,
          true,
        );

    checkAndUpdate(this);
  }

  addTopRemainingCrossPieces() {
    Object.values(Position).forEach(position => {
      const cp = this.top.getCrossPiece(position);
      if (!cp) {
        const type = getDefaultCrossPieceType(
          acceptedCrossPieceTypesForTop(this.lastShelf(), position),
        );
        this.top.addCrossPiece(
          position,
          position === Position.Left || position === Position.Right
            ? this.depth
            : this.width,
          undefined,
          type,
          this.width >= MIN_WIDTH_NEED_REINFORCE && position === Position.Front,
        );
      }
    });

    checkAndUpdate(this);
  }

  getName(language: string): string {
    return getCabinetName(this, language);
  }

  getDescription(language: string): string {
    return getCabinetDescription(this, language);
  }

  get names(): { [id: string]: string } {
    return getCabinetNames(this);
  }

  get descriptions(): { [id: string]: string } {
    return getCabinetDescriptions(this);
  }

  setFixToWall(value: boolean): void {
    this._fixToWall = value;
    const cp = this.top.getCrossPiece(Position.Back);
    if (!cp) {
      let type = CrossPieceType.UpperEmptyLowerPane;
      const underCP = this.lastShelf()
        .rack(this.lastShelf().racks.length - 1)
        .getCrossPiece(Position.Back);
      if (underCP && underCP.base.type === CrossPieceType.UpperDoorsLowerDoors)
        type = CrossPieceType.UpperEmptyLowerDoors;
      this.top.addCrossPiece(Position.Back, this.width, undefined, type);
    }
    this.top.updateCrossPiece(
      Position.Back,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      value,
    );
  }

  isFixedToWall(): boolean {
    const bcp = this.top.getCrossPiece(Position.Back);
    if (bcp && this.support !== CabinetSupport.ToHangScrew)
      return bcp.base.drill;
    return false;
  }
}
